import * as PopoverPrimitive from '@radix-ui/react-popover';
import {
  cloneElement,
  type ButtonHTMLAttributes,
  isValidElement,
  type KeyboardEvent,
  type ReactElement,
  type ReactNode,
  useId,
  useRef,
  useState,
} from 'react';

import type { IconName } from './icon-paths.js';
import { Icon } from './icons.js';
import { Button, IconButton, type IconButtonVariant } from './primitives.js';

export type SegmentedButtonsOption<K extends string> = {
  readonly disabled?: boolean;
  readonly icon?: IconName;
  readonly key: K;
  readonly label: string;
  readonly loading?: boolean;
};

/**
 * MD3 segmented buttons (A4): single or multi select, check on the active segment, RTL arrow keys.
 */
export function SegmentedButtons<K extends string>({
  disabled = false,
  label,
  mode = 'single',
  onChange,
  options,
  value,
}: {
  readonly disabled?: boolean;
  readonly label: string;
  readonly mode?: 'single' | 'multiple';
  readonly onChange: (next: K | readonly K[]) => void;
  readonly options: readonly SegmentedButtonsOption<K>[];
  readonly value: K | readonly K[] | undefined;
}) {
  const refs = useRef<(HTMLButtonElement | null)[]>([]);
  const selected = new Set(
    mode === 'multiple'
      ? Array.isArray(value)
        ? value
        : value === undefined
          ? []
          : [value]
      : value === undefined
        ? []
        : [value as K],
  );
  const selectedIndex = options.findIndex((option) => selected.has(option.key));
  const focusIndex = selectedIndex >= 0 ? selectedIndex : 0;

  function toggle(key: K) {
    if (mode === 'multiple') {
      const next = new Set(selected);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      onChange([...next]);
      return;
    }
    onChange(key);
  }

  function onKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    const moves: Record<string, number> = {
      ArrowDown: 1,
      ArrowLeft: 1,
      ArrowRight: -1,
      ArrowUp: -1,
    };
    const move = moves[event.key];
    if (move === undefined || options.length === 0) return;
    event.preventDefault();
    const next = (focusIndex + move + options.length) % options.length;
    const option = options[next];
    if (option !== undefined && !option.disabled && !option.loading) {
      if (mode === 'single') {
        onChange(option.key);
      }
      refs.current[next]?.focus();
    }
  }

  return (
    <div
      aria-label={label}
      className="saay-segmented saay-segmented--md3"
      data-mode={mode}
      onKeyDown={onKeyDown}
      role={mode === 'multiple' ? 'group' : 'radiogroup'}
    >
      {options.map((option, position) => {
        const active = selected.has(option.key);
        const segmentDisabled = disabled || option.disabled === true || option.loading === true;
        return (
          <button
            aria-checked={mode === 'single' ? active : undefined}
            aria-pressed={mode === 'multiple' ? active : undefined}
            aria-busy={option.loading === true || undefined}
            className="saay-segmented__segment"
            data-active={active || undefined}
            disabled={segmentDisabled}
            key={option.key}
            onClick={() => toggle(option.key)}
            ref={(element) => {
              refs.current[position] = element;
            }}
            role={mode === 'multiple' ? 'button' : 'radio'}
            tabIndex={
              selectedIndex >= 0 ? (position === selectedIndex ? 0 : -1) : position === 0 ? 0 : -1
            }
            type="button"
          >
            {active ? <Icon name="check" /> : null}
            {option.icon === undefined ? null : <Icon name={option.icon} />}
            {option.label}
            {option.loading === true ? (
              <span aria-hidden="true" className="saay-button__spinner" />
            ) : null}
          </button>
        );
      })}
    </div>
  );
}

export interface FabProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {
  readonly icon: IconName;
  readonly label: string;
  readonly loading?: boolean;
  readonly size?: 'md' | 'sm';
}

/** MD3 FAB (A3): 40 dp visual, 48 dp target, elevated surface. */
export function Fab({
  className,
  icon,
  label,
  loading = false,
  size = 'md',
  type = 'button',
  ...rest
}: FabProps) {
  return (
    <button
      {...rest}
      aria-busy={loading || undefined}
      aria-label={label}
      className={['saay-fab', 'saay-state', className].filter(Boolean).join(' ')}
      data-size={size}
      disabled={rest.disabled === true || loading}
      title={label}
      type={type}
    >
      {loading ? (
        <span aria-hidden="true" className="saay-button__spinner" />
      ) : (
        <Icon name={icon} />
      )}
    </button>
  );
}

export interface ExtendedFabProps extends FabProps {
  readonly collapsed?: boolean;
}

/** MD3 extended FAB (A3): icon plus label; set `collapsed` to icon-only. */
export function ExtendedFab({
  children,
  collapsed = false,
  className,
  icon,
  label,
  loading = false,
  size = 'md',
  type = 'button',
  ...rest
}: ExtendedFabProps & { readonly children: ReactNode }) {
  return (
    <button
      {...rest}
      aria-busy={loading || undefined}
      aria-label={label}
      className={['saay-fab', 'saay-fab--extended', 'saay-state', className]
        .filter(Boolean)
        .join(' ')}
      data-collapsed={collapsed || undefined}
      data-size={size}
      disabled={rest.disabled === true || loading}
      title={label}
      type={type}
    >
      {loading ? (
        <span aria-hidden="true" className="saay-button__spinner" />
      ) : (
        <Icon name={icon} />
      )}
      {collapsed ? null : <span className="saay-fab__label">{children}</span>}
    </button>
  );
}

export type MenuItemProps = {
  readonly children: ReactNode;
  readonly destructive?: boolean;
  readonly disabled?: boolean;
  readonly disabledReason?: string;
  readonly onSelect?: () => void;
  readonly selected?: boolean;
};

export function MenuItem({
  children,
  destructive = false,
  disabled = false,
  disabledReason,
  onSelect,
  selected = false,
}: MenuItemProps) {
  return (
    <button
      className="saay-menu__item"
      data-destructive={destructive || undefined}
      data-selected={selected || undefined}
      disabled={disabled}
      onClick={() => {
        if (disabled) return;
        onSelect?.();
      }}
      role="menuitem"
      title={disabled ? disabledReason : undefined}
      type="button"
    >
      {selected ? (
        <Icon name="check" />
      ) : (
        <span aria-hidden="true" className="saay-menu__item-spacer" />
      )}
      <span className="saay-menu__item-label">{children}</span>
      {disabled && disabledReason !== undefined ? (
        <span className="saay-menu__item-support">{disabledReason}</span>
      ) : null}
    </button>
  );
}

export function Menu({
  align = 'end',
  children,
  label,
  onOpenChange,
  open,
  trigger,
}: {
  readonly align?: 'start' | 'end';
  readonly children: ReactNode;
  readonly label: string;
  readonly onOpenChange?: (open: boolean) => void;
  readonly open?: boolean;
  readonly trigger: ReactElement<ButtonHTMLAttributes<HTMLButtonElement>>;
}) {
  const menuId = useId();
  const [internalOpen, setInternalOpen] = useState(false);
  const isOpen = open ?? internalOpen;

  function setOpen(next: boolean) {
    if (open === undefined) {
      setInternalOpen(next);
    }
    onOpenChange?.(next);
  }

  const triggerProps: ButtonHTMLAttributes<HTMLButtonElement> = {
    'aria-controls': menuId,
    'aria-expanded': isOpen,
    'aria-haspopup': 'menu',
    'aria-label': trigger.props['aria-label'] ?? label,
    className: ['saay-menu__trigger', trigger.props.className].filter(Boolean).join(' '),
  };
  const triggerNode = isValidElement(trigger) ? cloneElement(trigger, triggerProps) : trigger;

  // The panel renders in a portal over the page, so a table or card with hidden overflow never
  // clips it (owner); Radix flips it above the trigger when there is no room below.
  return (
    <PopoverPrimitive.Root onOpenChange={setOpen} open={isOpen}>
      <div className="saay-menu" data-align={align}>
        <PopoverPrimitive.Trigger asChild>{triggerNode}</PopoverPrimitive.Trigger>
      </div>
      <PopoverPrimitive.Portal>
        {/* An MD3 menu closes once an item is chosen (W4: it stayed open behind the dialog it opened). */}
        <PopoverPrimitive.Content
          align={align}
          className="saay-menu__panel"
          collisionPadding={8}
          dir="rtl"
          id={menuId}
          onClick={(event) => {
            const item = (event.target as HTMLElement).closest<HTMLButtonElement>(
              '[role="menuitem"]',
            );
            if (item !== null && !item.disabled) setOpen(false);
          }}
          role="menu"
          sideOffset={6}
        >
          {children}
        </PopoverPrimitive.Content>
      </PopoverPrimitive.Portal>
    </PopoverPrimitive.Root>
  );
}

export type SplitButtonAlternative = {
  readonly key: string;
  readonly label: string;
  readonly onSelect: () => void;
};

const SPLIT_MEMORY_KEY = 'saay-split-button-last';

export function SplitButton({
  alternatives,
  children,
  loading = false,
  memoryKey,
  onPrimary,
  ...primaryProps
}: {
  readonly alternatives: readonly SplitButtonAlternative[];
  readonly children: ReactNode;
  readonly loading?: boolean;
  /** Session key for remembering the last menu choice (A6). */
  readonly memoryKey?: string;
  readonly onPrimary: () => void;
} & Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'>) {
  const [menuOpen, setMenuOpen] = useState(false);
  const remembered =
    memoryKey === undefined
      ? undefined
      : (sessionStorage.getItem(`${SPLIT_MEMORY_KEY}:${memoryKey}`) ?? undefined);

  function pickAlternative(item: SplitButtonAlternative) {
    if (memoryKey !== undefined) {
      try {
        sessionStorage.setItem(`${SPLIT_MEMORY_KEY}:${memoryKey}`, item.key);
      } catch {
        // Private mode: memory is best-effort for this tab.
      }
    }
    item.onSelect();
    setMenuOpen(false);
  }

  const ordered =
    remembered === undefined
      ? alternatives
      : [
          ...alternatives.filter((item) => item.key === remembered),
          ...alternatives.filter((item) => item.key !== remembered),
        ];

  return (
    <div className="saay-split-button">
      <Button
        className="saay-split-button__primary"
        loading={loading}
        onClick={onPrimary}
        {...primaryProps}
      >
        {children}
      </Button>
      <Menu
        label="گزینه‌های دیگر"
        onOpenChange={setMenuOpen}
        open={menuOpen}
        trigger={
          <IconButton
            className="saay-split-button__menu"
            icon="chevron-down"
            label="گزینه‌های دیگر"
            variant="outlined"
          />
        }
      >
        {ordered.map((item) => (
          <MenuItem
            key={item.key}
            onSelect={() => pickAlternative(item)}
            selected={item.key === remembered}
          >
            {item.label}
          </MenuItem>
        ))}
      </Menu>
    </div>
  );
}

export function ButtonGroup({ children }: { readonly children: ReactNode }) {
  return (
    <div className="saay-button-group" role="group">
      {children}
    </div>
  );
}

/**
 * Dialog action row (Q2): in RTL the primary action sits on the visual left (inline-end).
 * Pass `primary` last; cancel/secondary slots render toward inline-start.
 */
export function DialogActions({
  primary,
  secondary,
  tertiary,
}: {
  readonly primary: ReactNode;
  readonly secondary?: ReactNode;
  readonly tertiary?: ReactNode;
}) {
  return (
    <div className="saay-dialog-actions">
      {tertiary}
      {secondary}
      {primary}
    </div>
  );
}

export type { IconButtonVariant };
